// @ts-strict-ignore
import {DomainGetResponse} from 'common/report/queries/app-api/legacy/org-types';
import {Dashboard as DashboardResult, Dashboard} from 'common/report/dashboards/types';
import {Section} from 'common/components/items-menu/types';
import {isVerifiedDomain} from 'common/util/string';
import {LocalizationStore} from 'common/i18n/store';

import {DashboardDigest, DigestRecurrence, ProjectMember, SerializedDashboardDigest} from './types';

export function isEmailDigest(digest: DashboardDigest): boolean {
  // We'll treat email as the base case so that if any digests show up with
  // neither type of subscriber those digests will be treated as email digests.
  return !isSlackDigest(digest);
}

export function isSlackDigest(digest: DashboardDigest): boolean {
  return Boolean(digest.slack_subscriptions.length);
}

export function isAsapDigest(digest: DashboardDigest): boolean {
  return digest.recur === DigestRecurrence.ASAP;
}

const DIGEST_RECURRENCE_TRACKING_NAME: {[digestRecurrence: number]: string} = {
  [DigestRecurrence.Daily]: `Daily`,
  [DigestRecurrence.ASAP]: `ASAP`,
  [DigestRecurrence.Weekly]: `Weekly`,
  [DigestRecurrence.Monthly]: `Monthly`,
  [DigestRecurrence.Quarterly]: `Quarterly`,
};

export function getDigestTrackingData(
  digest: Partial<DashboardDigest> | Partial<SerializedDashboardDigest>,
  membersMap: Map<string, ProjectMember>,
  i18n: LocalizationStore,
) {
  const internalRecipients = digest.recipients.filter((email) => membersMap.has(email));

  return {
    ...(`id` in digest ? {'digest id': digest.id} : {}),
    'dashboard id': digest.dashboard_id,
    'recipient count': digest.recipients.length,
    recurrence: i18n.print(DIGEST_RECURRENCE_TRACKING_NAME[digest.recur]) || `unknown`,
    timezone: digest.timezone,
    'external recipient count': digest.recipients.length - internalRecipients.length,
    'internal recipient count': internalRecipients.length,
    'slack channel count': digest.slack_subscriptions.length,
    'has email recipients': !!digest.recipients.length,
    'has slack channels': !!digest.slack_subscriptions.length,
  };
}

const isVerifiedDomainHelper = (input: string, domains: DomainGetResponse[]): boolean => {
  return domains.length > 0 ? isVerifiedDomain(input, domains) : true;
};

export const addExternalUserFn = (input: string, domains: DomainGetResponse[]): Section[] => {
  if (input && input.includes(`@`)) {
    return [
      {
        items: [
          {
            added: true,
            label: input,
            value: {email: input},
            icon: `plus`,
            tooltip: isVerifiedDomainHelper(input, domains)
              ? null
              : {
                  text: `External email address`,
                  'always-show': true,
                },
          },
        ],
      },
    ];
  }
  return [];
};

export function responseToDashboardObject(results: DashboardResult[]) {
  return results.map<Dashboard>((apiResult) => ({
    id: apiResult.id,
    title: apiResult.title,
    description: apiResult.description,
    creator_id: apiResult.creator_id,
    creator_name: apiResult.creator_name,
    creator_email: apiResult.creator_email,
    card_order: apiResult.card_order,
    cards: [],
    contents: {},
    time_filter: {},
    filters: [],
    breakdowns: [],
    modified: apiResult.modified,
    layout: {},

    // permissions
    can_view: apiResult.can_view,
    can_update_basic: apiResult.can_update_basic,
    can_share: apiResult.can_share,
    is_superadmin: apiResult.is_superadmin,
    allow_staff_override: apiResult.allow_staff_override,
  }));
}
